import React, { useState, useEffect, useRef } from "react";

/* ═══════════════════════════════════════════════════════════════════════
   Design tokens — same palette as TeacherReportsPage for consistency
   ═══════════════════════════════════════════════════════════════════════ */
const C = {
  navy: "#0f1b2d",
  blue: "#1d4ed8",
  blueSoft: "#e8f0fe",
  red: "#dc2626",
  redSoft: "#fff5f5",
  green: "#16a34a",
  greenSoft: "#f0fff4",
  amber: "#d97706",
  amberSoft: "#fffbeb",
  slate: "#64748b",
  slateLight: "#94a3b8",
  border: "#e2e8f0",
  bg: "#f4f6f9",
  white: "#ffffff",
  text: "#1a202c",
};

const SUBJECT_OPTIONS = [
  "English",
  "Urdu",
  "Mathematics",
  "Science",
  "Physics",
  "Chemistry",
  "Biology",
  "Computer Science",
  "Social Studies",
  "Islamiyat",
  "Arts",
  "Physical Education",
];

const EMPLOYMENT_STATUSES = ["Active", "On Leave", "Resigned", "Terminated"];

/* ─────────────── FIX 1: safeTrim — never crashes on null ─────────────── */
const safeTrim = (v) => (v === null || v === undefined ? "" : String(v).trim());

/* ─────────────── FIX 2: nullToEmpty — normalises backend nulls ────────── */
const nullToEmpty = (obj) => {
  const out = {};
  for (const key of Object.keys(obj)) {
    out[key] = obj[key] === null || obj[key] === undefined ? "" : obj[key];
  }
  return out;
};

const emptyForm = {
  name: "",
  father_name: "",
  gender: "",
  date_of_birth: "",
  cnic: "",
  contact_number: "",
  email: "",
  address: "",
  campus_id: "",
  subjects: [],
  other_subject: "",
  qualification: "",
  experience_years: "",
  joining_date: "",
  employment_status: "Active",
  emergency_contact_name: "",
  emergency_contact_number: "",
  salary: "",
  login_email: "",
  login_password: "",
};

/* ─── Styled sub-components ─── */

const SectionTitle = ({ children, required }) => (
  <div
    style={{
      fontSize: "13px",
      fontWeight: 800,
      color: C.navy,
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      margin: "28px 0 12px 0",
      paddingBottom: "8px",
      borderBottom: `2px solid ${C.border}`,
      display: "flex",
      alignItems: "center",
      gap: "6px",
    }}
  >
    {children}
    {required && (
      <span style={{ color: C.red, fontSize: "11px", fontWeight: 700 }}>
        * required
      </span>
    )}
  </div>
);

const FormLabel = ({ children, required }) => (
  <label
    style={{
      display: "block",
      fontSize: "11px",
      fontWeight: 700,
      color: C.slate,
      marginBottom: "5px",
      textTransform: "uppercase",
      letterSpacing: "0.4px",
    }}
  >
    {children}
    {required && <span style={{ color: C.red, marginLeft: "3px" }}>*</span>}
  </label>
);

const FormInput = ({ error, ...rest }) => (
  <input
    {...rest}
    style={{
      padding: "9px 13px",
      borderRadius: "8px",
      border: error ? `1.5px solid ${C.red}` : `1.5px solid ${C.border}`,
      fontSize: "13px",
      color: C.text,
      background: C.white,
      outline: "none",
      boxSizing: "border-box",
      width: "100%",
      ...(rest.style || {}),
    }}
  />
);

const FormSelect = ({ error, ...rest }) => (
  <select
    {...rest}
    style={{
      padding: "9px 13px",
      borderRadius: "8px",
      border: error ? `1.5px solid ${C.red}` : `1.5px solid ${C.border}`,
      fontSize: "13px",
      color: C.text,
      background: C.white,
      outline: "none",
      boxSizing: "border-box",
      width: "100%",
    }}
  />
);

const ErrorText = ({ children }) =>
  children ? (
    <span
      style={{
        display: "block",
        fontSize: "11px",
        color: C.red,
        marginTop: "4px",
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  ) : null;

const ActionBtn = ({ children, onClick, disabled, variant = "primary" }) => {
  const v = {
    primary: { background: C.navy, color: "#fff", border: "none" },
    ghost: {
      background: C.white,
      color: C.slate,
      border: `1.5px solid ${C.border}`,
    },
    danger: { background: C.red, color: "#fff", border: "none" },
  }[variant] || { background: C.navy, color: "#fff", border: "none" };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      type="button"
      style={{
        padding: "10px 22px",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        transition: "opacity .15s",
        ...v,
      }}
    >
      {children}
    </button>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   TeacherForm component
   ═══════════════════════════════════════════════════════════════════════ */
function TeacherForm({
  onSave,
  onCancel,
  initialData,
  showToast,
  campuses = [],
  canAssignCredentials = false,
}) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const submitLockRef = useRef(false);

  /* ─── FIX 3: Normalise null → "" before merging into form state ─── */
  useEffect(() => {
    if (initialData) {
      const cleaned = nullToEmpty(initialData);
      setForm({
        ...emptyForm,
        ...cleaned,
        subjects: Array.isArray(initialData.subjects)
          ? initialData.subjects
          : [],
        campus_id:
          initialData.campus_id != null ? String(initialData.campus_id) : "",
        login_email: initialData.login_email || "",
        login_password: "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [initialData]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const toggleSubject = (subject) => {
    setForm((prev) => {
      const has = prev.subjects.includes(subject);
      return {
        ...prev,
        subjects: has
          ? prev.subjects.filter((s) => s !== subject)
          : [...prev.subjects, subject],
      };
    });
    setErrors((prev) => ({ ...prev, subjects: undefined }));
  };

  /* ─── FIX 4: All .trim() replaced with safeTrim() ─── */
  const validate = () => {
    const next = {};

    if (!safeTrim(form.name)) next.name = "Full name is required";
    if (!form.gender) next.gender = "Gender is required";
    if (!safeTrim(form.cnic)) next.cnic = "CNIC is required";
    else if (!/^\d{5}-?\d{7}-?\d{1}$/.test(safeTrim(form.cnic)))
      next.cnic = "Enter a valid CNIC (e.g. 12345-1234567-1)";
    if (!safeTrim(form.contact_number))
      next.contact_number = "Contact number is required";
    if (!form.campus_id) next.campus_id = "Campus is required";
    if (form.subjects.length === 0 && !safeTrim(form.other_subject))
      next.subjects = "Select at least one subject";
    if (!safeTrim(form.qualification))
      next.qualification = "Qualification is required";
    if (!form.joining_date) next.joining_date = "Joining date is required";
    if (!form.employment_status)
      next.employment_status = "Employment status is required";

    if (
      safeTrim(form.email) &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeTrim(form.email))
    )
      next.email = "Enter a valid email";
    if (
      form.experience_years &&
      (isNaN(form.experience_years) || Number(form.experience_years) < 0)
    )
      next.experience_years = "Enter a valid number";
    if (form.salary && (isNaN(form.salary) || Number(form.salary) < 0))
      next.salary = "Enter a valid amount";

    if (canAssignCredentials) {
      if (!safeTrim(form.login_email)) {
        next.login_email = "Login email is required";
      } else if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeTrim(form.login_email))
      ) {
        next.login_email = "Enter a valid login email";
      }
      if (!initialData && !safeTrim(form.login_password)) {
        next.login_password = "Temporary password is required";
      } else if (form.login_password && form.login_password.length < 6) {
        next.login_password = "Password must be at least 6 characters";
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current) return;
    if (!validate()) {
      showToast &&
        showToast(
          "error",
          "Missing Information",
          "Please fix the highlighted fields.",
        );
      return;
    }

    const allSubjects = [...form.subjects];
    if (safeTrim(form.other_subject))
      allSubjects.push(safeTrim(form.other_subject));

    /* ─── FIX 5: All .trim() replaced with safeTrim() in payload ─── */
    const payload = {
      name: safeTrim(form.name),
      father_name: safeTrim(form.father_name) || null,
      gender: form.gender,
      date_of_birth: form.date_of_birth || null,
      cnic: safeTrim(form.cnic),
      contact_number: safeTrim(form.contact_number),
      email: safeTrim(form.email) || null,
      address: safeTrim(form.address) || null,
      campus_id: parseInt(form.campus_id, 10),
      subjects: allSubjects,
      qualification: safeTrim(form.qualification),
      experience_years: form.experience_years
        ? parseInt(form.experience_years, 10)
        : null,
      joining_date: form.joining_date,
      employment_status: form.employment_status,
      emergency_contact_name: safeTrim(form.emergency_contact_name) || null,
      emergency_contact_number: safeTrim(form.emergency_contact_number) || null,
      salary: form.salary ? parseFloat(form.salary) : null,
    };

    if (canAssignCredentials) {
      const existingLoginEmail = safeTrim(initialData?.login_email || "");
      const draftLoginEmail = safeTrim(form.login_email);
      const draftPassword = safeTrim(form.login_password);
      const loginEmailChanged =
        existingLoginEmail && draftLoginEmail !== existingLoginEmail;

      // On create, both fields are required.
      if (!initialData) {
        payload.login_email = draftLoginEmail;
        payload.login_password = draftPassword;
      } else if (existingLoginEmail) {
        // If the teacher already has credentials and the admin is only editing
        // teacher details, don't send credential fields unless the admin is
        // intentionally changing or resetting them.
        if (loginEmailChanged || draftPassword) {
          payload.login_email = draftLoginEmail;
          if (draftPassword) {
            payload.login_password = draftPassword;
          }
        }
      } else if (draftLoginEmail) {
        // Existing teacher with no linked login account: allow assignment now.
        payload.login_email = draftLoginEmail;
        if (draftPassword) {
          payload.login_password = draftPassword;
        }
      }
    }

    submitLockRef.current = true;
    setIsSaving(true);
    const success = await onSave(payload);
    setIsSaving(false);
    submitLockRef.current = false;
    if (success && !initialData) setForm(emptyForm);
  };

  /* ─── Render ─── */
  return (
    <div style={{ padding: "20px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <button
          onClick={onCancel}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 38,
            height: 38,
            borderRadius: "9px",
            border: `1.5px solid ${C.border}`,
            background: C.white,
            cursor: "pointer",
            color: C.slate,
            flexShrink: 0,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          ←
        </button>
        <h1
          style={{
            fontSize: "20px",
            fontWeight: 800,
            color: C.text,
            margin: 0,
          }}
        >
          {/* FIX 6: safeTrim on initialData.name too */}
          {initialData
            ? `Edit: ${safeTrim(initialData.name) || "Teacher"}`
            : "Register New Teacher"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div
          style={{
            background: C.white,
            borderRadius: "12px",
            border: `1px solid ${C.border}`,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          {/* Card header strip */}
          <div
            style={{
              background: C.navy,
              padding: "14px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#fff",
              }}
            >
              {initialData ? "Edit Teacher" : "New Teacher Registration"}
            </span>
            <div
              style={{
                display: "flex",
                gap: "10px",
              }}
            >
              <ActionBtn variant="ghost" onClick={onCancel}>
                Cancel
              </ActionBtn>
              <button
                type="submit"
                disabled={isSaving}
                style={{
                  padding: "10px 22px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 700,
                  cursor: isSaving ? "not-allowed" : "pointer",
                  opacity: isSaving ? 0.5 : 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "opacity .15s",
                  background: C.navy,
                  color: "#fff",
                  border: "none",
                }}
              >
                {isSaving
                  ? "Saving…"
                  : initialData
                    ? "Update Teacher"
                    : "Register Teacher"}
              </button>
            </div>
          </div>

          <div style={{ padding: "24px 28px" }}>
            {/* ─── Personal Details ─── */}
            <SectionTitle required>Personal Details</SectionTitle>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <FormLabel required>Full Name</FormLabel>
                <FormInput
                  type="text"
                  error={errors.name}
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="e.g. Ayesha Khan"
                />
                <ErrorText>{errors.name}</ErrorText>
              </div>

              <div>
                <FormLabel>Father's / Husband's Name</FormLabel>
                <FormInput
                  type="text"
                  value={form.father_name}
                  onChange={(e) => setField("father_name", e.target.value)}
                  placeholder="Optional"
                />
              </div>

              <div>
                <FormLabel required>Gender</FormLabel>
                <FormSelect
                  error={errors.gender}
                  value={form.gender}
                  onChange={(e) => setField("gender", e.target.value)}
                >
                  <option value="">Select…</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </FormSelect>
                <ErrorText>{errors.gender}</ErrorText>
              </div>

              <div>
                <FormLabel>Date of Birth</FormLabel>
                <FormInput
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => setField("date_of_birth", e.target.value)}
                />
              </div>

              <div>
                <FormLabel required>CNIC / National ID</FormLabel>
                <FormInput
                  type="text"
                  error={errors.cnic}
                  value={form.cnic}
                  onChange={(e) => setField("cnic", e.target.value)}
                  placeholder="12345-1234567-1"
                />
                <ErrorText>{errors.cnic}</ErrorText>
              </div>

              <div>
                <FormLabel required>Contact Number</FormLabel>
                <FormInput
                  type="tel"
                  error={errors.contact_number}
                  value={form.contact_number}
                  onChange={(e) => setField("contact_number", e.target.value)}
                  placeholder="03XX-XXXXXXX"
                />
                <ErrorText>{errors.contact_number}</ErrorText>
              </div>

              <div>
                <FormLabel>Email</FormLabel>
                <FormInput
                  type="email"
                  error={errors.email}
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder="Optional"
                />
                <ErrorText>{errors.email}</ErrorText>
              </div>

              <div
                style={{
                  gridColumn: "1 / -1",
                }}
              >
                <FormLabel>Address</FormLabel>
                <FormInput
                  type="text"
                  value={form.address}
                  onChange={(e) => setField("address", e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            {/* ─── Employment Details ─── */}
            <SectionTitle required>Employment Details</SectionTitle>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <FormLabel required>Campus</FormLabel>
                <FormSelect
                  error={errors.campus_id}
                  value={form.campus_id}
                  onChange={(e) => setField("campus_id", e.target.value)}
                >
                  <option value="">Select…</option>
                  {campuses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </FormSelect>
                <ErrorText>{errors.campus_id}</ErrorText>
                {campuses.length === 0 && (
                  <ErrorText>No campuses found. Add a campus first.</ErrorText>
                )}
              </div>

              <div>
                <FormLabel required>Qualification</FormLabel>
                <FormInput
                  type="text"
                  error={errors.qualification}
                  value={form.qualification}
                  onChange={(e) => setField("qualification", e.target.value)}
                  placeholder="e.g. M.Sc. Mathematics"
                />
                <ErrorText>{errors.qualification}</ErrorText>
              </div>

              <div>
                <FormLabel>Experience (years)</FormLabel>
                <FormInput
                  type="number"
                  min="0"
                  error={errors.experience_years}
                  value={form.experience_years}
                  onChange={(e) => setField("experience_years", e.target.value)}
                  placeholder="Optional"
                />
                <ErrorText>{errors.experience_years}</ErrorText>
              </div>

              <div>
                <FormLabel required>Joining Date</FormLabel>
                <FormInput
                  type="date"
                  error={errors.joining_date}
                  value={form.joining_date}
                  onChange={(e) => setField("joining_date", e.target.value)}
                />
                <ErrorText>{errors.joining_date}</ErrorText>
              </div>

              <div>
                <FormLabel required>Employment Status</FormLabel>
                <FormSelect
                  error={errors.employment_status}
                  value={form.employment_status}
                  onChange={(e) =>
                    setField("employment_status", e.target.value)
                  }
                >
                  {EMPLOYMENT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </FormSelect>
                <ErrorText>{errors.employment_status}</ErrorText>
              </div>

              <div>
                <FormLabel>Salary (PKR)</FormLabel>
                <FormInput
                  type="number"
                  min="0"
                  error={errors.salary}
                  value={form.salary}
                  onChange={(e) => setField("salary", e.target.value)}
                  placeholder="Optional"
                />
                <ErrorText>{errors.salary}</ErrorText>
              </div>
            </div>

            {/* ─── Login Credentials ─── */}
            <>
              <SectionTitle required>System Login Credentials</SectionTitle>
              <div
                style={{
                  background: canAssignCredentials ? C.blueSoft : C.redSoft,
                  border: `1px solid ${canAssignCredentials ? C.blue : C.red}`,
                  borderRadius: "8px",
                  padding: "16px 18px",
                  marginBottom: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: canAssignCredentials ? C.blue : C.red,
                    fontWeight: 700,
                    marginBottom: "12px",
                  }}
                >
                  {canAssignCredentials
                    ? "🔐 These credentials will allow the teacher to log into the ALS Portal."
                    : "🔒 Login credentials can only be managed by an admin or superadmin."}
                </div>

                {canAssignCredentials ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(240px, 1fr))",
                      gap: "18px",
                    }}
                  >
                    <div>
                      <FormLabel required>Login Email (Username)</FormLabel>
                      <FormInput
                        type="email"
                        error={errors.login_email}
                        value={form.login_email}
                        onChange={(e) =>
                          setField("login_email", e.target.value)
                        }
                        placeholder="teacher@example.com"
                      />
                      <ErrorText>{errors.login_email}</ErrorText>
                    </div>

                    <div>
                      <FormLabel required={!initialData}>
                        {initialData
                          ? "New Password (leave blank to keep current)"
                          : "Temporary Password"}
                      </FormLabel>
                      <FormInput
                        type="text"
                        error={errors.login_password}
                        value={form.login_password}
                        onChange={(e) =>
                          setField("login_password", e.target.value)
                        }
                        placeholder={
                          initialData ? "******" : "Set a temporary password"
                        }
                      />
                      <ErrorText>{errors.login_password}</ErrorText>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: "13px",
                      color: C.slate,
                      padding: "6px 0",
                    }}
                  >
                    You do not have permission to assign or edit login
                    credentials for this teacher.
                  </div>
                )}
              </div>
            </>

            {/* ─── Subjects Taught ─── */}
            <SectionTitle required>Subjects Taught</SectionTitle>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                gap: "10px",
                marginBottom: "10px",
              }}
            >
              {SUBJECT_OPTIONS.map((subject) => (
                <label
                  key={subject}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: `1px solid ${
                      form.subjects.includes(subject) ? C.blue : C.border
                    }`,
                    background: form.subjects.includes(subject)
                      ? C.blueSoft
                      : C.white,
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: form.subjects.includes(subject) ? 700 : 500,
                    color: form.subjects.includes(subject) ? C.blue : C.text,
                    transition: "all .15s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.subjects.includes(subject)}
                    onChange={() => toggleSubject(subject)}
                    style={{ accentColor: C.blue }}
                  />
                  {subject}
                </label>
              ))}
            </div>
            <div style={{ marginBottom: "8px" }}>
              <FormLabel>Other Subject (if not listed)</FormLabel>
              <FormInput
                type="text"
                value={form.other_subject}
                onChange={(e) => setField("other_subject", e.target.value)}
                placeholder="e.g. French"
              />
            </div>
            <ErrorText>{errors.subjects}</ErrorText>

            {/* ─── Emergency Contact ─── */}
            <SectionTitle>Emergency Contact (Optional)</SectionTitle>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <FormLabel>Contact Name</FormLabel>
                <FormInput
                  type="text"
                  value={form.emergency_contact_name}
                  onChange={(e) =>
                    setField("emergency_contact_name", e.target.value)
                  }
                  placeholder="Optional"
                />
              </div>

              <div>
                <FormLabel>Contact Number</FormLabel>
                <FormInput
                  type="tel"
                  value={form.emergency_contact_number}
                  onChange={(e) =>
                    setField("emergency_contact_number", e.target.value)
                  }
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>

          {/* Footer action bar */}
          <div
            style={{
              padding: "16px 28px",
              borderTop: `1px solid ${C.border}`,
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              background: C.bg,
            }}
          >
            <ActionBtn variant="ghost" onClick={onCancel}>
              Cancel
            </ActionBtn>
            <button
              type="submit"
              disabled={isSaving}
              style={{
                padding: "10px 22px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 700,
                cursor: isSaving ? "not-allowed" : "pointer",
                opacity: isSaving ? 0.5 : 1,
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                transition: "opacity .15s",
                background: C.navy,
                color: "#fff",
                border: "none",
              }}
            >
              {isSaving
                ? "Saving…"
                : initialData
                  ? "Update Teacher"
                  : "Register Teacher"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default TeacherForm;
